(() => {
  if (customElements.get('mc-tech-pants-benefits')) return;

  class McTechPantsBenefits extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;

      this._dialog = this.querySelector('dialog');
      const data = this.querySelector('[data-mc-benefits-data]');
      if (!this._dialog || !data) return;

      try {
        this._data = JSON.parse(data.textContent);
      } catch {
        return;
      }

      this._controller = new AbortController();
      const options = { signal: this._controller.signal };
      this._options = this._data.options || [];
      this._featuredMediaId = this._data.featuredMediaId;

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-mc-benefit]');
        if (trigger && this.contains(trigger)) {
          this._open(trigger);
        } else if (event.target.closest('[data-mc-benefits-close]')) {
          this._close();
        } else if (event.target === this._dialog) {
          const bounds = this._dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
            this._close();
          }
        }
      }, options);

      this._dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this._close();
      }, options);

      this._dialog.addEventListener('close', () => this._afterClose(), options);

      document.addEventListener('variant:change', (event) => {
        // A product form's input named "id" shadows its native .id property.
        if (event.target.getAttribute?.('id') !== this.getAttribute('form')) return;
        const variant = event.detail?.variant;
        if (!variant) return;
        this._options = variant.options || [];
        this._featuredMediaId = variant.featured_media?.id;
        if (this._dialog.open) this._renderPhotos();
      }, options);
    }

    disconnectedCallback() {
      this._clearPending();
      if (this._dialog?.open) this._dialog.close();
      this._afterClose();
      this._controller?.abort();
      this._controller = null;
    }

    _open(trigger) {
      // A hidden experiment must never open or affect page scrolling.
      if (!this.getClientRects().length || this._dialog.open) return;
      const selectedIndex = Number(trigger.dataset.mcBenefit);
      const benefit = this._data.benefits[selectedIndex];
      if (!benefit) return;

      this._trigger = trigger;
      this.querySelector('[data-mc-benefits-title]').textContent = benefit.label;
      this.querySelector('[data-mc-benefits-description]').textContent = benefit.description;
      this.querySelectorAll('[data-mc-benefit-detail]').forEach((detail) => {
        detail.hidden = Number(detail.dataset.mcBenefitDetail) === selectedIndex;
      });
      trigger.setAttribute('aria-expanded', 'true');
      this._renderPhotos();
      this._dialog.inert = false;
      this._dialog.dataset.state = 'opening';
      this._dialog.showModal();
      this._dialog.scrollTop = 0;
      this._previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this._scrollLocked = true;

      // Establish the translated starting frame before the entrance transition.
      this._dialog.getBoundingClientRect();
      this._openFrame = requestAnimationFrame(() => {
        this._dialog.dataset.state = 'open';
        this._openFrame = null;
      });

      if ('ResizeObserver' in window) {
        this._visibilityObserver = new ResizeObserver(() => {
          if (this._dialog.open && !this.getClientRects().length) this._close(true);
        });
        this._visibilityObserver.observe(this);
      }
    }

    _close(immediate = false) {
      if (!this._dialog?.open || this._dialog.dataset.state === 'closing') return;
      this._clearPending();
      this._dialog.dataset.state = 'closing';

      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this._dialog.close();
        return;
      }

      // Keep the native focus trap until the exit animation has finished.
      this._closeTimer = window.setTimeout(() => {
        this._closeTimer = null;
        if (this._dialog.open) this._dialog.close();
      }, 310);
    }

    _afterClose() {
      this._clearPending();
      this._visibilityObserver?.disconnect();
      this._visibilityObserver = null;
      if (this._dialog) {
        this._dialog.inert = true;
        delete this._dialog.dataset.state;
      }
      if (this._scrollLocked) {
        document.documentElement.style.overflow = this._previousOverflow;
        this._scrollLocked = false;
      }
      if (this._trigger) {
        this._trigger.setAttribute('aria-expanded', 'false');
        if (this._trigger.isConnected && this._trigger.getClientRects().length) {
          this._trigger.focus({ preventScroll: true });
        }
        this._trigger = null;
      }
    }

    _clearPending() {
      if (this._openFrame != null) cancelAnimationFrame(this._openFrame);
      if (this._closeTimer != null) window.clearTimeout(this._closeTimer);
      this._openFrame = null;
      this._closeTimer = null;
    }

    _renderPhotos() {
      const media = this._data.media || [];
      const normalise = (value) => String(value || '').trim().toLocaleLowerCase();
      const selectedColor = normalise(this._options[this._data.colorOptionIndex]);
      const colorGroup = (item) => {
        const match = String(item.alt || '').match(/#(?:color|colour|kleur|couleur|farbe|colore|kolor)_(.+)$/i);
        return match ? normalise(match[1]) : '';
      };
      const matching = media.filter((item) => colorGroup(item) === selectedColor && selectedColor);
      const featured = media.find((item) => String(item.id) === String(this._featuredMediaId));
      const ungrouped = media.filter((item) => !colorGroup(item));
      const candidates = matching.length ? matching : [featured, ...ungrouped].filter(Boolean);
      const photos = candidates.filter((item, index, items) => items.findIndex((candidate) => candidate.id === item.id) === index).slice(0, 2);

      const photoContainer = this.querySelector('[data-mc-benefits-photos]');
      photoContainer.hidden = photos.length === 0;
      photoContainer.dataset.photoCount = String(photos.length);
      this.querySelectorAll('[data-mc-benefit-photo]').forEach((slot, index) => {
        const photo = photos[index];
        slot.hidden = !photo;
        if (!photo || slot.dataset.mediaId === String(photo.id)) return;

        const image = document.createElement('img');
        image.src = photo.src;
        image.width = photo.width;
        image.height = photo.height;
        image.decoding = 'async';
        image.alt = String(photo.alt || '').replace(/#.*$/, '').trim() || `The Tech Pants in ${this._options[this._data.colorOptionIndex] || 'your selected colour'}, view ${index + 1}`;
        slot.replaceChildren(image);
        slot.dataset.mediaId = String(photo.id);
      });
    }
  }

  customElements.define('mc-tech-pants-benefits', McTechPantsBenefits);
})();
